import { inject, Injectable } from '@angular/core';

import { AttributeDataService } from '../../services/attribute-data.service';
import { StorageUtilsService } from 'src/app/storage/services/storage-utils.service';

export interface HuggingBoxCustomization {
  top: string;
  left: string;
  width: string;
  height: string;
  hugApplied: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ImageDimensionsService {
  // Matches the on-canvas footprint of the generic default box (300x200)
  // this replaces, so newly-added images don't appear conspicuously larger
  // or smaller than components placed the old way.
  static readonly TARGET_AREA = 300 * 200;
  // Matches the timeout ResourceLoaderService already uses for image loads
  // elsewhere in this app.
  static readonly LOAD_TIMEOUT_MS = 10000;

  private storageUtilsService = inject(StorageUtilsService);
  private attributeDataService = inject(AttributeDataService);

  // Resolves once with a hugging box for the image at `source` (a
  // "bucket/name" storage path), or null if its dimensions can't be
  // determined (load failure) — callers should fall back to the default box.
  getHuggingBox(source: string): Promise<HuggingBoxCustomization | null> {
    const url = this.storageUtilsService.storageFileUrlFor(source);

    return this._loadNaturalSize(url).then((size) =>
      size ? this._computeBox(size.width, size.height) : null,
    );
  }

  private _loadNaturalSize(
    url: string,
  ): Promise<{ width: number; height: number } | null> {
    return new Promise((resolve) => {
      const img = new Image();
      let settled = false;

      const finish = (result: { width: number; height: number } | null) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timeoutId);
        resolve(result);
      };

      // A stalled storage request never fires onload/onerror on its own —
      // bound the wait so callers aren't left hanging indefinitely.
      const timeoutId = setTimeout(
        () => finish(null),
        ImageDimensionsService.LOAD_TIMEOUT_MS,
      );

      img.onload = () =>
        finish({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => finish(null);
      img.src = url;
    });
  }

  private _computeBox(
    naturalWidth: number,
    naturalHeight: number,
  ): HuggingBoxCustomization | null {
    if (!naturalWidth || !naturalHeight) {
      return null;
    }

    const aspectRatio = naturalWidth / naturalHeight;
    const targetHeight = Math.sqrt(
      ImageDimensionsService.TARGET_AREA / aspectRatio,
    );
    const targetWidth = targetHeight * aspectRatio;

    const { width: canvasWidth, height: canvasHeight } =
      this.attributeDataService.getCanvasDimensions();

    // Never scale up — only shrink the fixed-footprint box, preserving
    // aspect ratio, when it would otherwise overflow a small/custom canvas.
    const scale = Math.min(
      1,
      canvasWidth / targetWidth,
      canvasHeight / targetHeight,
    );
    const width = targetWidth * scale;
    const height = targetHeight * scale;

    return {
      top: `${Math.round((canvasHeight - height) / 2)}px`,
      left: `${Math.round((canvasWidth - width) / 2)}px`,
      width: `${Math.round(width)}px`,
      height: `${Math.round(height)}px`,
      hugApplied: true,
    };
  }
}
